import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import DashboardToolbar from '../components/dashboard/DashboardToolbar';
import DashboardKpiCard from '../components/dashboard/DashboardKpiCard';
import { useMaintenanceData } from '../hooks/useDashboardData';
import {
  ALL_YEARS, getYearsFromMaintenance,
  getPeriodLabel, groupByWorkType, isOpenRecord, getComparisonPeriod, buildKpiComparison,
  filterMaintenanceByDept, getDepartmentsFromMaintenance,
  getDefaultDashboardPeriod, collectPeriodKeysFromMaintenance, getPrimaryPeriod,
  getDashboardDefaultFilters,
  filterPeriodsByYear, filterMaintenanceDashboard, periodToYearMonth, toFilterArray, isNoSelection,
} from '../utils/dashboardUtils';
import { exportMaintenance } from '../utils/dashboardExport';
import {
  WorkTypePieChart, DeptWorkTypeStackedChart, DailyTrendChart,
  CompletedOpenWorkTypeChart, OpenByDeptDonutChart, MachineTotalCompletedChart,
  MachineMonthlyClusterChart,
} from '../components/dashboard/DashboardCharts';

const Panel = ({ title, children, colors, span = 3 }) => (
  <Box gridColumn={`span ${span}`} gridRow="span 2" bgcolor={colors.primary[400]} p="10px" display="flex" flexDirection="column" overflow="hidden">
    <Typography color={colors.grey[100]} variant="subtitle2" fontWeight={600} mb={0.5} flexShrink={0}>{title}</Typography>
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, height: '230px' }}>{children}</Box>
  </Box>
);

const prunePeriods = (selected, available) => {
  if (isNoSelection(selected)) return selected;
  const valid = selected.filter((p) => available.includes(p));
  if (valid.length) return valid;
  const def = getDefaultDashboardPeriod();
  if (available.includes(def)) return [def];
  return available.length ? [available[available.length - 1]] : [];
};

const MaintenanceDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { records, loading, error, refresh } = useMaintenanceData();
  const [years, setYears] = useState(() => getDashboardDefaultFilters('maintenance').years);
  const [periods, setPeriods] = useState(() => [getDefaultDashboardPeriod()]);
  const [departments, setDepartments] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const yearList = useMemo(() => getYearsFromMaintenance(records), [records]);
  const yearOptions = useMemo(() => yearList.map((y) => ({ value: String(y), label: String(y) })), [yearList]);
  const allPeriods = useMemo(() => collectPeriodKeysFromMaintenance(records), [records]);
  const periodKeys = useMemo(() => filterPeriodsByYear(allPeriods, years, 'calendar'), [allPeriods, years]);
  const periodOptions = useMemo(() => periodKeys.map((p) => ({ value: p, label: p })), [periodKeys]);
  const departmentList = useMemo(() => getDepartmentsFromMaintenance(records), [records]);
  const filtered = useMemo(
    () => filterMaintenanceByDept(filterMaintenanceDashboard(records, years, periods, 'calendar'), departments),
    [records, years, periods, departments],
  );
  const periodLabel = getPeriodLabel(years, periods);
  const cmp = useMemo(() => getComparisonPeriod(years, periods), [years, periods]);
  const { filterYear, month } = useMemo(() => {
    const yArr = toFilterArray(years, ALL_YEARS);
    const primary = getPrimaryPeriod(periods);
    const p = periodToYearMonth(primary);
    return {
      filterYear: yArr.length === 1 ? yArr[0] : p.year,
      month: p.month,
    };
  }, [years, periods]);

  const handleYearsChange = useCallback((newYears) => {
    setYears(newYears);
    const next = filterPeriodsByYear(allPeriods, newYears, 'calendar');
    setPeriods((prev) => prunePeriods(prev, next));
  }, [allPeriods]);

  useEffect(() => {
    setPeriods((prev) => {
      const valid = prev.filter((p) => periodKeys.includes(p));
      if (valid.length) return valid.length === prev.length ? prev : valid;
      return prunePeriods(prev, periodKeys);
    });
  }, [periodKeys]);

  const currRecords = useMemo(() => filterMaintenanceByDept(
    filterMaintenanceDashboard(records, ALL_YEARS, cmp.currentLabel, 'calendar'), departments,
  ), [records, cmp.currentLabel, departments]);
  const prevRecords = useMemo(() => filterMaintenanceByDept(
    filterMaintenanceDashboard(records, ALL_YEARS, cmp.prevLabel, 'calendar'), departments,
  ), [records, cmp.prevLabel, departments]);

  const groups = groupByWorkType(filtered);
  const open = (wt) => groups.find((g) => g.Department === wt)?.openCount || 0;
  const closed = filtered.filter((r) => !isOpenRecord(r)).length;

  const totalKpi = buildKpiComparison(currRecords.length, prevRecords.length);
  const completedKpi = buildKpiComparison(
    currRecords.filter((r) => !isOpenRecord(r)).length,
    prevRecords.filter((r) => !isOpenRecord(r)).length,
  );
  const openKpi = buildKpiComparison(
    currRecords.filter(isOpenRecord).length,
    prevRecords.filter(isOpenRecord).length,
  );
  const mcKpi = buildKpiComparison(
    currRecords.filter((r) => isOpenRecord(r) && r.WorkType === 'Machine Maintenance').length,
    prevRecords.filter((r) => isOpenRecord(r) && r.WorkType === 'Machine Maintenance').length,
  );
  const nwKpi = buildKpiComparison(
    currRecords.filter((r) => isOpenRecord(r) && r.WorkType === 'New Work').length,
    prevRecords.filter((r) => isOpenRecord(r) && r.WorkType === 'New Work').length,
  );
  const emKpi = buildKpiComparison(
    currRecords.filter((r) => isOpenRecord(r) && r.WorkType === 'Electrical Maintenance').length,
    prevRecords.filter((r) => isOpenRecord(r) && r.WorkType === 'Electrical Maintenance').length,
  );

  const kpis = [
    { label: 'Total Task', value: filtered.length },
    { label: 'Completed Task', value: closed },
    { label: 'Open Task', value: filtered.filter(isOpenRecord).length },
    { label: 'M/C Maint. Open', value: open('Machine Maintenance') },
    { label: 'New Work Open', value: open('New Work') },
    { label: 'Elec. Maint. Open', value: open('Electrical Maintenance') },
  ];

  const handleDownload = async () => {
    setDownloading(true);
    try { await exportMaintenance(filtered, periodLabel, kpis); } finally { setDownloading(false); }
  };

  const handleRefresh = useCallback(() => {
    const defaults = getDashboardDefaultFilters('maintenance');
    setYears(defaults.years);
    setPeriods(defaults.periods);
    setDepartments(defaults.departments);
    refresh();
  }, [refresh]);

  if (error) {
    return (
      <Box ml="200px" mt="60px" p={2} width="calc(100% - 220px)">
        <Typography color="error">Failed to load maintenance data: {error}</Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Ensure MIS backend is running at {process.env.REACT_APP_MISBACKEND_URL}
        </Typography>
      </Box>
    );
  }

  return (
    <Box ml="200px" mt={0} pl={2} width="calc(100% - 220px)">
      <DashboardToolbar
        variant="maintenance"
        title="Maintenance Report"
        subtitle={`Task Summary of period → ${periodLabel}; comparing with previous month`}
        years={years} periods={periods} yearOptions={yearOptions} periodOptions={periodOptions}
        onYearsChange={handleYearsChange} onPeriodsChange={setPeriods}
        periodLabel={periodLabel}
        departments={departments} onDepartmentsChange={setDepartments} departmentOptions={departmentList}
        onDownload={handleDownload} onRefresh={handleRefresh}
        downloading={downloading} loading={loading}
      />
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gridAutoRows="110px" gap="5px">
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Total Task" value={totalKpi.current}
              prevLabel={cmp.prevLabel} prevValue={totalKpi.previous} pct={totalKpi.pct} increased={totalKpi.increased} />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Completed Task" value={completedKpi.current}
              prevLabel={cmp.prevLabel} prevValue={completedKpi.previous} pct={completedKpi.pct} increased={completedKpi.increased} />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Open Task" value={openKpi.current}
              prevLabel={cmp.prevLabel} prevValue={openKpi.previous} pct={openKpi.pct} increased={openKpi.increased} />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="M/C Maint. (Open Task)" value={mcKpi.current}
              prevLabel={cmp.prevLabel} prevValue={mcKpi.previous} pct={mcKpi.pct} increased={mcKpi.increased} />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="New Work (Open Task)" value={nwKpi.current}
              prevLabel={cmp.prevLabel} prevValue={nwKpi.previous} pct={nwKpi.pct} increased={nwKpi.increased} />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Elec. Maintenance (Open Task)" value={emKpi.current}
              prevLabel={cmp.prevLabel} prevValue={emKpi.previous} pct={emKpi.pct} increased={emKpi.increased} />
          </Box>

          <Panel title="Total Task by WorkType" colors={colors} span={3}>
            <WorkTypePieChart records={filtered} />
          </Panel>
          <Panel title="Total Task by Department and WorkType" colors={colors} span={3}>
            <DeptWorkTypeStackedChart records={filtered} />
          </Panel>
          <Panel title="Daily Trend" colors={colors} span={3}>
            <DailyTrendChart records={filtered} year={filterYear} month={month} />
          </Panel>
          <Panel title="Completed/Open Task by WorkType" colors={colors} span={3}>
            <CompletedOpenWorkTypeChart records={filtered} />
          </Panel>

          <Panel title="Open Task by Department" colors={colors} span={4}>
            <OpenByDeptDonutChart records={filtered} />
          </Panel>
          <Panel title="Machine Wise Total / Completed Task" colors={colors} span={4}>
            <MachineTotalCompletedChart records={filtered} />
          </Panel>
          <Panel title="Monthly Total Task Distribution per Machine" colors={colors} span={4}>
            <MachineMonthlyClusterChart records={filtered} />
          </Panel>
        </Box>
      )}
    </Box>
  );
};

export default MaintenanceDashboard;
